import { Skeleton } from "@atlant/ui"

import type { MonthSummary } from "../../components/month-card.tsx"

// en-US, as the month cards: en-GB abbreviates September as "Sept"
const monthFormat = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" })
const months = Array.from({ length: 12 }, (_, i) =>
  monthFormat.format(new Date(Date.UTC(2000, i, 1))),
)

// Leaves room for the value label above the tallest bar
const MAX_BAR_PX = 56

interface LateByMonthChartProps {
  summaries: MonthSummary[]
  isLoading: boolean
}

// Uses the server's month grouping, so each bar matches its month card
export const LateByMonthChart = ({ summaries, isLoading }: LateByMonthChartProps) => {
  if (isLoading) return <Skeleton className="h-24 w-full" />

  const counts = months.map(
    (_, i) => summaries.find((s) => s.month === i + 1)?.lateCount ?? 0,
  )
  const max = Math.max(...counts, 1)

  return (
    <ol aria-label="Late invoices by service month" className="flex">
      {counts.map((count, i) => {
        const label = `${months[i]}: ${count} late ${count === 1 ? "invoice" : "invoices"}`
        return (
          <li
            key={months[i]}
            aria-label={label}
            title={label}
            className="flex min-w-0 flex-1 flex-col items-center border-l px-0.5 first:border-l-0"
          >
            <div className="flex h-20 w-full flex-col items-center justify-end gap-0.5">
              <span className="text-foreground text-xs font-semibold tabular-nums">
                {count}
              </span>
              {count > 0 && (
                <div
                  className="bg-primary/40 w-full max-w-6 rounded-t-[4px]"
                  style={{ height: `${Math.max((count / max) * MAX_BAR_PX, 2)}px` }}
                />
              )}
            </div>
            <span className="text-muted-foreground mt-1 text-[11px]">{months[i]}</span>
          </li>
        )
      })}
    </ol>
  )
}
